import React, { useMemo, useState } from 'react';

// OL
import OlBaseLayer from 'ol/layer/Base';

// MUI
import { Box, Card, CardActionArea, CardContent, CardMedia, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrintDisabledIcon from '@mui/icons-material/PrintDisabled';

// Custom
// import MapContext, { MapContextType } from '@/context/MapContext/MapContext';

export interface BaseLayerTreeUIProps {
  mapId?: number;
  layers: Array<OlBaseLayer>;
  // className?: string;
}

const BaseLayerTreeUI = (props: BaseLayerTreeUIProps) => {
  const { mapId, layers } = props;

  const theme = useTheme();
  const { t } = useTranslation();

  const [, setRefreshCounter] = useState(0);

  const handleChangeLayer = (layer: OlBaseLayer, layersArray: OlBaseLayer[]) => {
    layersArray.forEach((v) => v.setVisible(false));
    layer.setVisible(!layer.getVisible());
    setRefreshCounter((prev) => prev + 1);

    const mapLayerId = mapId || 1;

    try {
      const storedBaseLayers = localStorage.getItem('mapbaselayers');
      const parsedBaseLayers = storedBaseLayers ? JSON.parse(storedBaseLayers) : {};
      parsedBaseLayers[mapLayerId] = layer.get('code');
      localStorage.setItem('mapbaselayers', JSON.stringify(parsedBaseLayers));
    } catch (error) {
      // In case of an error (e.g., invalid JSON in localStorage), initialize a new entry
      const newLocalStorageEntry: { [key: number]: string } = {};
      newLocalStorageEntry[mapLayerId] = layer.get('code');
      localStorage.setItem('mapbaselayers', JSON.stringify(newLocalStorageEntry));
    }
  };

  // Group by group ttoken
  const groupedLayers = useMemo(() => {
    const groups: Record<string, { sortorder: number; layers: OlBaseLayer[] }> = {};

    let sortOrder = 1;
    layers.forEach((layer) => {
      const groupToken = layer.get('groupTtoken') || 'map:layer.group.other';

      if (!groups[groupToken]) {
        groups[groupToken] = { sortorder: sortOrder, layers: [] };
        sortOrder += 1;
      }

      groups[groupToken].layers.push(layer);
    });

    return groups;
  }, [layers]);

  return (
    <Stack gap={1} p={1}>
      {Object.entries(groupedLayers)
        .sort(([, a], [, b]) => a.sortorder - b.sortorder)
        .map(([groupToken, groupLayers]) => (
          <Box key={groupToken}>
            {/* Group Title */}
            <Typography variant="h6" pb={1}>
              {t(groupToken)}
            </Typography>
            {/* Cards */}
            <Grid container spacing={1}>
              {groupLayers.layers.map((layer) => {
                const isSelected = layer.getVisible();

                const descIzvor = layer.get('desc_izvor');
                const descGodina = layer.get('desc_godina');

                return (
                  <Grid size={{ xs: 12 }} key={`${layer.get('id')}-base-layer-card`}>
                    <Card
                      elevation={isSelected ? 3 : 1}
                      sx={{
                        border: '2px solid',
                        borderColor: isSelected ? theme.palette.primary.main : theme.palette.background.default,
                        transition: 'all 0.1s ease-in-out',
                        boxSizing: 'border-box',
                        '&:hover': {
                          boxShadow: 5,
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleChangeLayer(layer, layers)}
                        // sx={{
                        //   display: 'flex',
                        //   flexDirection: 'column',
                        //   alignItems: 'center',
                        // }}
                      >
                        {layer.get('code') !== 'no_layer' ? (
                          <CardMedia
                            component="img"
                            image={layer.getProperties().preview}
                            alt={layer.get('id')}
                            sx={{ width: '100%', height: 140 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 140,
                              bgcolor: theme.palette.background.paper,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          />
                        )}

                        <CardContent sx={{ p: 1, position: "relative" }}>
                          {!layer.get('allow_print') && (
                            <Tooltip title={t('map:tooltips.no_print')} placement="top" arrow>
                              <PrintDisabledIcon sx={{ position: 'absolute', bottom: 6, right: 6, fontSize: 18, color: 'grey.400' }} />
                            </Tooltip>
                          )}
                          <Typography
                            variant="body1"
                            fontWeight={isSelected ? 600 : 400}
                            color={isSelected ? theme.palette.primary.main : 'text.primary'}
                          >
                            {t(layer.get('title_ttoken'))}
                          </Typography>
                          {descIzvor ? (
                            <Typography
                              variant="caption"
                              component="p"
                              fontWeight={isSelected ? 600 : 400}
                              color={isSelected ? theme.palette.primary.main : 'text.primary'}
                            >
                              {t('map:baselayer.izvor')}: {descIzvor}
                            </Typography>
                          ) : null}
                          {descGodina ? (
                            <Typography
                              variant="caption"
                              component="p"
                              fontWeight={isSelected ? 600 : 400}
                              color={isSelected ? theme.palette.primary.main : 'text.primary'}
                            >
                              {t('map:baselayer.godina')}: {descGodina}
                            </Typography>
                          ) : null}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
    </Stack>
  );
};

export default BaseLayerTreeUI;
